import { Alert, Box, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";

/**
 * Small chart primitives for the dashboard, drawn as inline SVG / plain boxes.
 *
 * Deliberately dependency-free: the app already hand-rolls its Levey-Jennings plot the same way, and
 * a charting library would add ~150 kB to a bundle that renders four figures. Everything takes its
 * colours from the theme so the dark palette works without a second set of hex codes.
 */

/** ₹ short form on the Indian scale — axis ticks and KPI hints have no room for full amounts. */
export function compactInr(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k`;
  return `₹${Math.round(n)}`;
}

/**
 * Card chrome every panel shares, including the four states a panel can be in. An empty panel says
 * why it is empty — a blank rectangle reads as a broken screen, which is exactly how the old
 * placeholder dashboard read.
 */
export function ChartPanel({
  title, subtitle, loading = false, error, isEmpty = false,
  emptyText = "Nothing to show yet.", height = 240, children,
}: {
  title: string;
  subtitle?: ReactNode;
  loading?: boolean;
  error?: string | null;
  isEmpty?: boolean;
  emptyText?: string;
  height?: number;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
      <Box
        sx={{
          flexGrow: 1, minHeight: height,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={height - 24} />
        ) : error ? (
          <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>
        ) : isEmpty ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", px: 2 }}>
            {emptyText}
          </Typography>
        ) : (
          <Box sx={{ width: "100%" }}>{children}</Box>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Vertical bars — a value per day/category
// ---------------------------------------------------------------------------

export interface BarDatum {
  label: string;
  value: number;
  /** Native tooltip on hover — the full, unrounded reading behind the bar. */
  tooltip?: string;
}

export function BarChart({
  data, formatValue = (n: number) => String(n), height = 220, labelEvery = 1,
}: {
  data: BarDatum[];
  formatValue?: (n: number) => string;
  height?: number;
  /** Print every n-th x label. 14 daily labels collide at narrow widths; every 2nd reads fine. */
  labelEvery?: number;
}) {
  const t = useTheme();
  const W = 720, H = height;
  const padL = 58, padR = 10, padT = 10, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(...data.map(d => d.value), 0);
  const scale = max > 0 ? innerH / max : 0;
  const slot = innerW / Math.max(data.length, 1);
  const barW = Math.min(slot * 0.62, 44);
  const ticks = max > 0 ? [0, max / 2, max] : [0];
  const yOf = (v: number) => padT + innerH - v * scale;

  return (
    <svg
      width="100%" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`Bar chart, ${data.length} points, maximum ${formatValue(max)}`}
      style={{ display: "block" }}
    >
      {ticks.map(v => (
        <g key={v}>
          <line
            x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)}
            stroke={t.palette.divider} strokeDasharray={v === 0 ? "" : "3 3"}
          />
          <text
            x={padL - 8} y={yOf(v) + 4} textAnchor="end"
            fontSize={10} fill={t.palette.text.secondary}
          >
            {formatValue(v)}
          </text>
        </g>
      ))}

      {data.map((d, i) => {
        const h = d.value > 0 ? Math.max(d.value * scale, 2) : 0;
        return (
          <g key={`${d.label}-${i}`}>
            {h > 0 && (
              <rect
                x={padL + i * slot + (slot - barW) / 2}
                y={padT + innerH - h}
                width={barW} height={h} rx={2}
                fill={t.palette.primary.main}
              >
                <title>{d.tooltip ?? `${d.label}: ${formatValue(d.value)}`}</title>
              </rect>
            )}
            {i % labelEvery === 0 && (
              <text
                x={padL + i * slot + slot / 2} y={H - 8}
                textAnchor="middle" fontSize={10} fill={t.palette.text.secondary}
              >
                {d.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Donut — a whole split into parts, with the total in the middle
// ---------------------------------------------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function ringPath(cx: number, cy: number, rOut: number, rIn: number, start: number, end: number) {
  const large = end - start > 180 ? 1 : 0;
  const a = polar(cx, cy, rOut, start), b = polar(cx, cy, rOut, end);
  const c = polar(cx, cy, rIn, end), d = polar(cx, cy, rIn, start);
  return `M ${a.x} ${a.y} A ${rOut} ${rOut} 0 ${large} 1 ${b.x} ${b.y} ` +
         `L ${c.x} ${c.y} A ${rIn} ${rIn} 0 ${large} 0 ${d.x} ${d.y} Z`;
}

export function DonutChart({
  slices, formatValue = (n: number) => String(n), centerLabel, size = 172,
}: {
  slices: DonutSlice[];
  formatValue?: (n: number) => string;
  /** Small caption under the total in the hole, e.g. "at risk". */
  centerLabel?: string;
  size?: number;
}) {
  const t = useTheme();
  const shown = slices.filter(s => s.value > 0);
  const total = shown.reduce((sum, s) => sum + s.value, 0);
  const cx = size / 2, cy = size / 2;
  const rOut = size / 2 - 4, rIn = rOut * 0.62;

  let cursor = 0;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }} spacing={2}
      alignItems="center" justifyContent="center"
    >
      <svg
        width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
        aria-label={`Donut chart totalling ${formatValue(total)}`}
        style={{ flexShrink: 0 }}
      >
        {/* One slice can't be drawn as an arc — start and end land on the same point. */}
        {shown.length === 1 ? (
          <>
            <circle cx={cx} cy={cy} r={(rOut + rIn) / 2} fill="none"
              stroke={shown[0].color} strokeWidth={rOut - rIn}>
              <title>{`${shown[0].label}: ${formatValue(shown[0].value)}`}</title>
            </circle>
          </>
        ) : (
          shown.map(s => {
            const sweep = (s.value / total) * 360;
            const path = ringPath(cx, cy, rOut, rIn, cursor, cursor + sweep);
            cursor += sweep;
            return (
              <path key={s.label} d={path} fill={s.color}
                stroke={t.palette.background.paper} strokeWidth={1}>
                <title>{`${s.label}: ${formatValue(s.value)} (${Math.round((s.value / total) * 100)}%)`}</title>
              </path>
            );
          })
        )}
        <text
          x={cx} y={cy - 2} textAnchor="middle" fontSize={16} fontWeight={700}
          fill={t.palette.text.primary}
        >
          {formatValue(total)}
        </text>
        {centerLabel && (
          <text x={cx} y={cy + 14} textAnchor="middle" fontSize={9.5} fill={t.palette.text.secondary}>
            {centerLabel}
          </text>
        )}
      </svg>

      <Stack spacing={0.75} sx={{ minWidth: 0, flexGrow: 1 }}>
        {shown.map(s => (
          <Stack key={s.label} direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: s.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
              {s.label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {formatValue(s.value)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 34, textAlign: "right" }}>
              {total > 0 ? `${Math.round((s.value / total) * 100)}%` : "0%"}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bars — a ranked list where the label needs room to breathe
// ---------------------------------------------------------------------------

export interface HBarRow {
  label: string;
  value: number;
  color?: string;
  /** Secondary line under the label — the context that makes the number actionable. */
  hint?: string;
}

export function HBarList({
  rows, formatValue = (n: number) => String(n),
}: {
  rows: HBarRow[];
  formatValue?: (n: number) => string;
}) {
  const t = useTheme();
  const max = Math.max(...rows.map(r => r.value), 0);

  return (
    <Stack spacing={1.25}>
      {rows.map((r, i) => (
        <Box key={`${r.label}-${i}`}>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="caption" sx={{ flexGrow: 1, minWidth: 0 }} noWrap title={r.label}>
              {r.label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {formatValue(r.value)}
            </Typography>
          </Stack>
          <Box sx={{ mt: 0.5, height: 8, borderRadius: 1, bgcolor: t.palette.action.hover }}>
            <Box
              sx={{
                height: "100%", borderRadius: 1,
                width: `${max > 0 ? Math.max((r.value / max) * 100, r.value > 0 ? 2 : 0) : 0}%`,
                bgcolor: r.color ?? t.palette.primary.main,
              }}
            />
          </Box>
          {r.hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {r.hint}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
